/**
 * 파일명 : usr0103m.tsx
 * 설명 : 사용자목록 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2020-08-10   윤성수     최초작성
 * 1.1       2024-03-19   김시현     직원명 콤보박스 추가
 * 1.2       2024-03-20   기라성     엔터키 조회 추가
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Usr0103mAction } from '../../../redux/slice/usr/usr0103m_slice';
import { IUserItemState, IUserListItemState, IUserStCdState, IDeptCdState } from '../../../redux/models/usr/usr0103m_model';


/*  AntD  */
import { Button, Space, Select, Row, Col, Layout, Typography, Collapse, Table } from 'antd';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';

/*  업무  */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';
import ExportExcelFile from 'components/exportExcelFile';



const Usr0103m = () => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
     const dispatch = useAppDispatch();
     const userList     = useAppSelector((state) => state.Usr0103m.userList);
     const userStCdList = useAppSelector((state) => state.Usr0103m.userStCdList);
     const deptCdList   = useAppSelector((state) => state.Usr0103m.deptCdList);
     const inquiryCnds  = useAppSelector((state) => state.Usr0103m.inquiryCnds);
     const userDatas    = useAppSelector((state) => state.Usr0103m.userDatas);
     const current      = useAppSelector((state) => state.Usr0103m.current);
     const pageSize     = useAppSelector((state) => state.Usr0103m.pageSize);

    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        fn_ResetResult();
        if (!userList     || userList.length < 1)     fn_User();
        if (!deptCdList   || deptCdList.length < 1)   fn_DeptCds();
        if (!userStCdList || userStCdList.length < 1) setUserStCdList();
    }, []);


    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text } = Typography;
    const { Panel } = Collapse;


    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>상태</div>,
                dataIndex: 'userStNm',
                key: 'userStNm',
                align: 'center',
                width: '6%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>성명</div>,
                dataIndex: 'userNm',
                key: 'userNm',
                align: 'left',
                width: '15%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>입사일자</div>,
                dataIndex: 'joinDt',
                key: 'joinDt',
                align: 'center',
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>퇴사일자</div>,
                dataIndex: 'leaveDtm',
                key: 'leaveDtm',
                align: 'center',
                render: (text:string) => text ? commonDate.maskDate(text, 'YYYY-MM-DD') : '재직중',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직원ID</div>,
                dataIndex: 'userId',
                key: 'userId',
                align: 'left',
                width: '15%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직위</div>,
                dataIndex: 'postNm',
                key: 'postNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직책</div>,
                dataIndex: 'dutyNm',
                key: 'dutyNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>부서</div>,
                dataIndex: 'deptNm',
                key: 'deptNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>휴대폰</div>,
                dataIndex: 'mobileNo',
                key: 'mobileNo',
                align: 'center',
                width: '12%',
                render: (_:string, record:IUserListItemState) => {
                    let mobileNo = record.mobileNo;
                    mobileNo = mobileNo.replace(/-/gi, '');
                    if (!mobileNo) return '';
                    let patternPhone = /01[016789]-?\d{3,4}-?\d{4}$/;
                    if (!patternPhone.test(mobileNo)) {
                        mobileNo = '유효하지 않은 휴대폰번호';
                    } else {
                        mobileNo = mobileNo.replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, `$1-$2-$3`);
                    }
    
                    return mobileNo;
                }
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>상세보기</div>,
                key: 'action',
                render: (_:string, record:IUserListItemState) => (
                    common.isAdmin() || common.getEmpNo() === record.empNo ?
                    <Button
                        id='btn_showDetail'
                        name='btn_showDetail'
                        className='background-color-default-button'
                        size='small'
                        onClick={(e) => onClick(e, record)}
                    >
                        상세보기
                    </Button> : null
                ),
                align: 'center',
                width: '6%',
            }];
            return arr;
    }, []);

     const excelColumns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>성명</div>,
                dataIndex: 'userNm',
                key: 'userNm',
                align: 'left',
                width: '15%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직위</div>,
                dataIndex: 'postNm',
                key: 'postNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>부서</div>,
                dataIndex: 'deptNm',
                key: 'deptNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>휴대폰</div>,
                dataIndex: 'mobileNo',
                key: 'mobileNo',
                align: 'center',
                width: '12%',
                render: (_:string, record:IUserListItemState) => {
                    let mobileNo = record.mobileNo;
                    mobileNo = mobileNo.replace(/-/gi, '');
                    if (!mobileNo) return '';
                    let patternPhone = /01[016789]-?\d{3,4}-?\d{4}$/;
                    if (!patternPhone.test(mobileNo)) {
                        mobileNo = '유효하지 않은 휴대폰번호';
                    } else {
                        mobileNo = mobileNo.replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, `$1-$2-$3`);
                    }
    
                    return mobileNo;
                }
            },

            {
                title: () => <div style={{ textAlign: 'center' }}>주소</div>,
                dataIndex: 'addr',
                key: 'addr',
                align: 'left',
                width: '50%',
            },

            {
                title: () => <div style={{ textAlign: 'center' }}>배송지주소</div>,
                dataIndex: 'dlvAddr',
                key: 'dlvAddr',
                align: 'left',
                width: '50%',
            },
        ];
            return arr;
    }, []);


    /**
     * 기능 : fn_User
     * 설명 : 직원명 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_User = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            userStCd: 'A'
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0003',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.userList.sort((a:IUserItemState, b:IUserItemState) => (a.userNm > b.userNm) ? 1 : -1)
                dispatch(Usr0103mAction.setUserList(responseData.userList));
            },
        });
    };

    const userOptions = React.useCallback(() => {
        let arrUserOptions: IUserItemState[] = [];

        if (common.isAdmin()) {
            arrUserOptions.push({label: '전체', value: ''});
        }

        userList.forEach((item:IUserItemState) => {
            arrUserOptions.push({
                label: item.userNm,
                value: item.empNo
            });
        });

        return arrUserOptions;
    }, [userList]);



    /**
     * 기능 : fn_DeptCds
     * 설명 : 부서코드 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_DeptCds = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            deptStCd: 'A'
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRCOMR0005',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.deptItems.sort((a:IDeptCdState, b:IDeptCdState) => (a.deptNm > b.deptNm) ? 1 : -1)
                dispatch(Usr0103mAction.setDeptCdList(responseData.deptItems));
            },
        });
    };

    const deptOptions = React.useCallback(() => {
        let arrOptions: IDeptCdState[] = [];

        if (common.isAdmin()) {
            arrOptions.push({label: '전체', value: ''});
        }

        deptCdList.forEach((item:IDeptCdState) => {
            arrOptions.push({
                label: item.deptNm,
                value: item.deptCd
            });
        });

        return arrOptions;
    }, [deptCdList]);


    const setUserStCdList = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:IUserStCdState[]) => {
                let arrOptions:IUserStCdState[] = [];

                arrOptions.push({label: '전체', value: ''});

                datas.forEach((item:IUserStCdState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd
                    });
                });

                dispatch(Usr0103mAction.setUserStCdList(arrOptions));
            }
        };
        common.getComCodeList('003', option);
    }

    React.useEffect(() => {
        dispatch(Usr0103mAction.setInquiryCnds({ ...inquiryCnds, userStCd : 'A' }));
        setTimeout(() => {
            fn_Search();
        }, 300);
    }, [userStCdList]);

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Usr0103mAction.setCurrent(1));
        dispatch(Usr0103mAction.setUserDatas([]));
    };


    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any, record:IUserListItemState) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_Search': {
                fn_ResetResult();   // 결과초기화
                fn_Search();        // 조회
                break;
            }

            case 'btn_Reset': {
                dispatch(Usr0103mAction.initState());
                break;
            }

            case 'btn_addUser': {
                const params:any = {};
                params.title = '사용자추가';
                params.width = 1020;
                params.popupId = 'usr0103p1';
                common.openPopup(params);
                break;
            }
            case 'btn_showDetail': {
                const params:any = {};
                params.title = '사용자관리';
                params.width = 1020;
                params.popupId = 'usr0103p1';
                params.props = { empNo : record.empNo };
                common.openPopup(params);
                break;
            }

            default:
        }
    };

    /**
     * 기능 : 조회
     * param : 
     */
    const fn_Search  = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            ...inquiryCnds
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0003',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.userList.sort((a:IUserItemState, b:IUserItemState) => (a.userNm > b.userNm) ? 1 : -1);
                dispatch(Usr0103mAction.setUserDatas(responseData.userList));
            },
        });
    };


    /**
      * 기능 : 입력 후 Enter key 입력 시 조회
      * 설명 : 입력컴포넌트
      * param : 
      * return : None
      */
    const handleOnKeyUp = (e:any) => {
        if (e.key === 'Enter') {
            fn_Search(); // 조회
        }
    };

    const excelDatas = userDatas.map((item:IUserListItemState) => ({
        ...item,

        addr:
            (item.userPostNo ? item.userPostNo + ' ' : '') +
            (item.userAddr || '') +
            (item.userDtlAddr ? ', ' + item.userDtlAddr : ''),

        dlvAddr:
            (item.userDlvPostNo ? item.userDlvPostNo + ' ' : '') +
            (item.userDlvAddr || '') +
            (item.userDlvDtlAddr ? ', ' + item.userDlvDtlAddr : '')
    }));

    /* 엑셀다운로드 */
    const excelParams = {
        datas: excelDatas,
        btnName: '엑셀파일 내려받기',
        fileName: '직원정보_' + commonDate.getToday(),
        sheetName: '직원정보_'+ commonDate.getToday(),
        columns: excelColumns(),
    }

    return (
        <Layout>
            <Content>
                <Collapse
                    bordered={false}
                    defaultActiveKey={['1']}
                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                    className='site-collapse-custom-collapse'
                >
                    <Panel header='검색조건' key='1' className='site-collapse-custom-panel'>
                        <Row
                            align='middle'
                            justify='space-between'
                            style={{ marginTop: 10 }}
                        >
                            <Space>
                                <Col>
                                    <Space>
                                        <label>직원명</label>
                                        <Select 
                                            showSearch
                                            style={{
                                                width: 200,
                                            }}
                                            optionFilterProp='label'
                                            defaultValue=''
                                            filterSort={(optionA: any, optionB: any) => {
                                                const labelA = (optionA?.label ?? '').toLowerCase();
                                                const labelB = (optionB?.label ?? '').toLowerCase();
                                                
                                                if (labelA === '전체') return -1;
                                                if (labelB === '전체') return 1;
                                                
                                                return labelA.localeCompare(labelB);
                                            }}
                                            options={userOptions()}
                                            onKeyUp={handleOnKeyUp}
                                            value={inquiryCnds.empNo}
                                            onChange={val => dispatch(Usr0103mAction.setInquiryCnds({...inquiryCnds, empNo: val}))}
                                        ></Select>
                                    </Space>
                                </Col>
                                <Col>
                                    <Space>
                                        <label style={{ marginLeft : 10 }}>부서</label>
                                        <Select
                                            style={{
                                                width: 200,
                                            }}
                                            optionFilterProp='label'
                                            options={deptOptions()}
                                            onKeyUp={handleOnKeyUp}
                                            onChange={val => {
                                                dispatch(Usr0103mAction.setInquiryCnds({...inquiryCnds, deptCd: val}))
                                            }}
                                            value={inquiryCnds.deptCd}
                                        />
                                    </Space>
                                </Col>
                                <Col>
                                    <Space>
                                        <label>직원상태</label>
                                        <Select
                                            style={{
                                                width: 200,
                                            }}
                                            optionFilterProp='label'
                                            options={userStCdList}
                                            onKeyUp={handleOnKeyUp}
                                            onChange={val => {
                                                dispatch(Usr0103mAction.setInquiryCnds({...inquiryCnds, userStCd: val}))
                                            }}
                                            value={inquiryCnds.userStCd}
                                        />
                                    </Space>
                                </Col>
                            </Space>
                            <Col>
                                <Space>
                                    <Button
                                        name='btn_Reset'
                                        className='background-color-default-button'
                                        onClick={(e:any) => onClick(e, null)}
                                    >
                                        초기화
                                </Button>
                                    <Button
                                        name='btn_Search'
                                        className='background-color-primary-button'
                                        icon={<SearchOutlined />}
                                        onClick={(e:any) => onClick(e, null)}
                                    >
                                        조회
                                </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Panel>
                </Collapse>
                <Row align='middle' style={{ height: 50 }}>
                    <Col flex='9.5' >
                        <Space>
                            <img className='title-icon' alt='' />
                            <Text strong>사용자목록</Text>
                        </Space>
                    </Col>
                    <Col flex='0.5' style={{ marginRight: 15 }}>
                        <Space>
                            {common.isAdmin() ?
                            <>
                            <ExportExcelFile excelParams={excelParams}/>
                            <Button
                                className='background-color-primary-button'
                                onClick={(e:any) => onClick(e, null)}
                                name='btn_addUser'
                            >
                                + 사용자추가
                            </Button>
                            </>
                            : null}
                        </Space>
                    </Col>
                </Row>
                <Row>
                    <Table
                        dataSource={userDatas}
                        key='empNo'
                        columns={columns()}
                        size='small'
                        style={{width:'100%'}}
                        scroll={{y:550}}
                        pagination={{
                            pageSize: pageSize,
                            current: current,
                            showTotal : () => '총 ' + userDatas.length + ' 건',
                            onChange: (page, pageSize) => {
                                dispatch(Usr0103mAction.setCurrent(page));
                                dispatch(Usr0103mAction.setPageSize(pageSize));
                            },
                        }}
                    />
                </Row>
            </Content>
        </Layout>
    );
};

export default Usr0103m;

